"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.toQuoteError = exports.offerDollarsToCents = void 0;
exports.getRefiPayoff = getRefiPayoff;
exports.getRefiPayoffWithFeeCents = getRefiPayoffWithFeeCents;
exports.getRefiPayoffFeeCents = getRefiPayoffFeeCents;
exports.getAppliedDebtSwapPayoff = getAppliedDebtSwapPayoff;
exports.quoteOffer = quoteOffer;
exports.cashAndPayoffForLineSizeCents = cashAndPayoffForLineSizeCents;
exports.tryQuoteOffer = tryQuoteOffer;
exports.lineSizeForCashAndPayoffCents = lineSizeForCashAndPayoffCents;
exports.lineSizeForNetCashCents = lineSizeForNetCashCents;
const assertUtils_1 = require("../assertUtils");
const offer_1 = require("./offer");
const math_1 = require("./slider/math");
const pricingBands_1 = require("./slider/pricingBands");
/** Whole cents of a dollar amount, rounded exactly (no binary floating point). */
const offerDollarsToCents = (dollars) => (0, math_1.roundDecimalProduct)(dollars, 100);
exports.offerDollarsToCents = offerDollarsToCents;
const roundHalfEvenSum = (products) => (0, math_1.roundHalfEven)((0, math_1.sumOfProducts)(products));
/** With no required upfront draw, Python's estimates assume the applicant draws the full line. */
const FULL_LINE_DRAWN = 1;
function fail(offer, message) {
    throw new Error(`Offer ${offer.policyName}${offer.identifier ? ` (${offer.identifier})` : ''}: ${message}`);
}
function required(offer, field, value) {
    if (typeof value !== 'number' || !Number.isFinite(value)) {
        fail(offer, `missing ${field}`);
    }
    return value;
}
/** Principal of every refinanced lien: no fees, no buffers. Zero when the offer does not refinance. */
function getRefiPayoff(terms) {
    const refi = terms.lienRefiOfferTerms;
    if (!refi) {
        if (terms.contingencies?.includes(offer_1.OfferContingency.priorLienRelease)) {
            fail(terms, 'refinance offer has no lienRefiOfferTerms');
        }
        return { totalCents: 0, liens: [] };
    }
    const liens = refi.refiLiensDetails.map((lien) => ({
        lienPosition: lien.lienPosition,
        balanceCents: (0, exports.offerDollarsToCents)(lien.balanceAmount),
        lenderName: lien.lenderName,
    }));
    const totalCents = liens.reduce((sum, lien) => sum + lien.balanceCents, 0);
    if (totalCents !== (0, exports.offerDollarsToCents)(refi.totalBalance)) {
        fail(terms, `lienRefiOfferTerms.totalBalance ${refi.totalBalance} does not match its liens (${totalCents} cents)`);
    }
    return { totalCents, liens };
}
/**
 * The one combined "payoff incl. fee" figure, in cents: the quoted principal plus the prior-lien payoff fee on it (a revolving
 * refinance's `lienRefiOfferTerms.payoffFee`). Only for a layout that shows the two as one number; every other display shows the
 * principal and the fee as their own lines (see `getRefiPayoffFeeCents`). Principal and fee round once, ties to even, like
 * Python's `lienRefiOfferTerms.payoffAmountWithFee`.
 */
function getRefiPayoffWithFeeCents(refiPayoff, payoffFee) {
    return roundHalfEvenSum([[refiPayoff.totalCents], [refiPayoff.totalCents, payoffFee]]);
}
/** The prior-lien payoff fee on its own line, in cents, so the fee line and principal line sum to `getRefiPayoffWithFeeCents`. */
function getRefiPayoffFeeCents(refiPayoff, payoffFee) {
    return getRefiPayoffWithFeeCents(refiPayoff, payoffFee) - refiPayoff.totalCents;
}
/** The quoted offer's prior-lien payoff fee: `getRefiPayoffFeeCents` at the refinance's own `payoffFee`, or 0 without a refinance. */
function getQuotedRefiPayoffFeeCents(terms, refiPayoff) {
    const refi = terms.lienRefiOfferTerms;
    return refi ? getRefiPayoffFeeCents(refiPayoff, required(terms, 'lienRefiOfferTerms.payoffFee', refi.payoffFee)) : 0;
}
/** Balances of the debt swap underwriting applied to the offer, never the user's current selection. */
function getAppliedDebtSwapPayoff(terms) {
    const debtSwap = terms.appliedDebtSwapOfferTerms;
    if (!debtSwap) {
        if (terms.appliedSelectedDebtSwapPersonalFinancialLiabilityIds?.length) {
            // An offer stored before the terms existed: reading no debt swap would overstate the cash to the applicant.
            fail(terms, 'offer applied a debt swap (appliedSelectedDebtSwapPersonalFinancialLiabilityIds) but has no appliedDebtSwapOfferTerms');
        }
        return { totalCents: 0, tradelines: [] };
    }
    const tradelines = debtSwap.tradelines.map((tradeline) => ({
        personalFinancialLiabilityId: tradeline.personalFinancialLiabilityId,
        balanceCents: (0, exports.offerDollarsToCents)(tradeline.balanceAmount),
        lenderName: tradeline.lenderName,
    }));
    const totalCents = tradelines.reduce((sum, tradeline) => sum + tradeline.balanceCents, 0);
    if (totalCents !== (0, exports.offerDollarsToCents)(debtSwap.totalBalance)) {
        fail(terms, `appliedDebtSwapOfferTerms.totalBalance ${debtSwap.totalBalance} does not match its tradelines (${totalCents} cents)`);
    }
    return { totalCents, tradelines };
}
function readQuoteInputs(offer) {
    const originationFeePercent = required(offer, 'originationFeePercent', offer.originationFeePercent);
    // Python leaves the first-draw fee null on offers that are not fixed-term; there is no first draw to charge.
    const firstDrawFeePercent = offer.fixedTermMonths == null ? (offer.firstDrawFeePercent ?? 0) : required(offer, 'firstDrawFeePercent', offer.firstDrawFeePercent);
    for (const [name, percent] of [
        ['originationFeePercent', originationFeePercent],
        ['firstDrawFeePercent', firstDrawFeePercent],
    ]) {
        if (percent < 0 || percent >= 1) {
            fail(offer, `${name} ${percent} must be a ratio in [0, 1)`);
        }
    }
    // Which recording policy a band follows decides real money, so an offer that predates the flag is not guessed at.
    if (offer.lineSizePricingBands?.some((band) => typeof band.recordingTaxFree !== 'boolean')) {
        fail(offer, 'a pricing band is missing recordingTaxFree');
    }
    return {
        offer,
        refiPayoff: getRefiPayoff(offer),
        debtSwapPayoff: getAppliedDebtSwapPayoff(offer),
        originationFeePercent,
        firstDrawFeePercent,
        recordingFixedCents: (0, exports.offerDollarsToCents)(required(offer, 'recordingChargesFixedComponentDollars', offer.recordingChargesFixedComponentDollars)),
        recordingVariableDecimal: required(offer, 'recordingChargesVariableComponentDecimal', offer.recordingChargesVariableComponentDecimal),
        // Absent on offers that predate signing charges; Python holds the billed signing charge at 0 today.
        effectiveSigningCents: (0, exports.offerDollarsToCents)(offer.effectiveSigningCharges ?? 0),
    };
}
/** The fraction of the line Python's `_getUpfrontDrawAmount` draws. A fixed-amount draw has none, so the inverse cannot use it. */
function fractionOfLineDrawn(offer) {
    const config = offer.upfrontDrawConfig;
    if (config == null) {
        return FULL_LINE_DRAWN;
    }
    switch (config.upfrontCashOutAmountCalculationMethod) {
        case offer_1.UpfrontCashOutAmountCalculationMethod.NoUpfrontCashOut:
            return FULL_LINE_DRAWN;
        case offer_1.UpfrontCashOutAmountCalculationMethod.PercentageOfLineSize:
            return config.minLineDrawDownForActivation;
        case offer_1.UpfrontCashOutAmountCalculationMethod.SpecificAmount:
            return fail(offer, 'a SpecificAmount draw does not scale with the line, so no line size can be derived from cash');
        default:
            return (0, assertUtils_1.assertNever)(config, `Offer ${offer.identifier}: unknown upfront draw config ${JSON.stringify(config)}`);
    }
}
/** Python `_getUpfrontDrawAmount`: the one draw every fee, cash and principal figure is based on. */
function computeDrawAmountCents({ offer }, lineSizeCents) {
    const config = offer.upfrontDrawConfig;
    const method = config?.upfrontCashOutAmountCalculationMethod;
    let drawAmountCents;
    switch (config?.upfrontCashOutAmountCalculationMethod) {
        case undefined:
        case offer_1.UpfrontCashOutAmountCalculationMethod.NoUpfrontCashOut:
            drawAmountCents = lineSizeCents;
            break;
        case offer_1.UpfrontCashOutAmountCalculationMethod.PercentageOfLineSize:
            drawAmountCents = (0, math_1.roundDecimalProduct)(lineSizeCents, config.minLineDrawDownForActivation);
            break;
        case offer_1.UpfrontCashOutAmountCalculationMethod.SpecificAmount:
            drawAmountCents = (0, exports.offerDollarsToCents)(config.requiredDrawAmountForActivation);
            break;
        default:
            return fail(offer, `unknown upfront draw method ${String(method)}`);
    }
    const isUpfrontCashOut = config != null && method !== offer_1.UpfrontCashOutAmountCalculationMethod.NoUpfrontCashOut;
    if (drawAmountCents > lineSizeCents || (isUpfrontCashOut && drawAmountCents <= 0)) {
        fail(offer, `upfront draw ${drawAmountCents} cents is outside (0, line ${lineSizeCents} cents]`);
    }
    return drawAmountCents;
}
/**
 * Recording billed at a line size. A banded offer computes it per band from the components, with a recordingTaxFree
 * band billing none. A non-banded offer quoted at its own line takes Python's billed `effectiveRecordingCharges`, which
 * already applies the state recording-tax waiver (STATE_TAX_LINE_SIZE_ZERO_TAX_CUTOFF, getEffectiveOfferClosingCharges_).
 */
function computeRecordingFeeCents(inputs, lineSizeCents, band) {
    const { offer } = inputs;
    if (band == null && typeof offer.lineSize === 'number' && lineSizeCents === (0, exports.offerDollarsToCents)(offer.lineSize)) {
        return (0, exports.offerDollarsToCents)(required(offer, 'effectiveRecordingCharges', offer.effectiveRecordingCharges));
    }
    if (band?.recordingTaxFree) {
        return 0;
    }
    // Python getClosingChargesForLineSize: fixed + variable * line, rounded once. For a non-banded offer at a line other
    // than its own this is not what Python bills (it would not apply the waiver): Python only prices the offer's own line.
    return roundHalfEvenSum([
        [lineSizeCents, inputs.recordingVariableDecimal],
        [inputs.recordingFixedCents, 1],
    ]);
}
/** The draw as exact factors: Python never rounds a percentage draw, only the cash figures computed from it. */
function drawFactors({ offer }, lineSizeCents, drawAmountCents) {
    const config = offer.upfrontDrawConfig;
    return config?.upfrontCashOutAmountCalculationMethod === offer_1.UpfrontCashOutAmountCalculationMethod.PercentageOfLineSize ? [lineSizeCents, config.minLineDrawDownForActivation] : [drawAmountCents];
}
/**
 * Fees, closing charges, net cash and cash + payoff at a line size.
 * Python `calcCashOutAfterPayoffAndFees`: both fees always apply.
 */
function computeCharges(inputs, lineSizeCents, band) {
    const drawAmountCents = computeDrawAmountCents(inputs, lineSizeCents);
    const draw = drawFactors(inputs, lineSizeCents, drawAmountCents);
    const upfrontFeesCents = roundHalfEvenSum([
        [...draw, inputs.firstDrawFeePercent],
        [lineSizeCents, inputs.originationFeePercent],
    ]);
    const originationFeeCents = roundHalfEvenSum([[lineSizeCents, inputs.originationFeePercent]]);
    const firstDrawFeeCents = upfrontFeesCents - originationFeeCents;
    const recordingFeeCents = computeRecordingFeeCents(inputs, lineSizeCents, band);
    const closingChargesCents = recordingFeeCents + inputs.effectiveSigningCents;
    // Python rounds this whole sum once rather than each fee, so a half-cent tie settles the same way.
    const netCashBeforeDebtSwapCents = roundHalfEvenSum([
        draw,
        [...draw, -inputs.firstDrawFeePercent],
        [lineSizeCents, -inputs.originationFeePercent],
        [-closingChargesCents, 1],
        [-inputs.refiPayoff.totalCents, 1],
    ]);
    const netCashCents = netCashBeforeDebtSwapCents - inputs.debtSwapPayoff.totalCents;
    const cashAndPayoffCents = netCashCents + inputs.refiPayoff.totalCents + inputs.debtSwapPayoff.totalCents;
    return {
        drawAmountCents,
        upfrontFeesCents,
        originationFeeCents,
        firstDrawFeeCents,
        recordingFeeCents,
        closingChargesCents,
        netCashCents,
        cashAndPayoffCents,
    };
}
/** The band for the line size. A banded offer must have one: a line outside every band is not priceable. */
function resolvePricingBand(offer, lineSizeCents) {
    if (!offer.lineSizePricingBands?.length) {
        return null;
    }
    return (0, pricingBands_1.resolveLineSizePricingBandForLineSizeCents)(offer.lineSizePricingBands, lineSizeCents) ?? fail(offer, `line size ${lineSizeCents} cents is outside every pricing band`);
}
function offerPricing(offer) {
    return {
        apr: required(offer, 'apr', offer.apr),
        margin: required(offer, 'margin', offer.margin),
        aprPreDiscount: offer.aprPreDiscount ?? null,
        fixedTermApr: offer.fixedTermApr ?? null,
        fixedTermAprPreDiscount: offer.fixedTermAprPreDiscount ?? null,
        primePromoApr: offer.primePromoApr ?? null,
        primePromoMargin: offer.primePromoMargin ?? null,
        primePromoAprPreDiscount: offer.primePromoAprPreDiscount ?? null,
    };
}
/**
 * Every figure of the offer at a line size, throwing rather than defaulting a missing input.
 * The state recording-tax waiver (STATE_TAX_LINE_SIZE_ZERO_TAX_CUTOFF) reaches the quote through banded offers'
 * recordingTaxFree bands; a non-banded offer quoted at its own line takes Python's billed effectiveRecordingCharges.
 */
function quoteOffer(offer, lineSizeCents) {
    if (!Number.isInteger(lineSizeCents) || lineSizeCents <= 0) {
        fail(offer, `line size ${lineSizeCents} cents must be a positive whole number of cents`);
    }
    const inputs = readQuoteInputs(offer);
    const pricingBand = resolvePricingBand(offer, lineSizeCents);
    const pricing = pricingBand?.pricing ?? offerPricing(offer);
    const { drawAmountCents, upfrontFeesCents, originationFeeCents, firstDrawFeeCents, recordingFeeCents, closingChargesCents, netCashCents } = computeCharges(inputs, lineSizeCents, pricingBand);
    const charges = { drawAmountCents, upfrontFeesCents, originationFeeCents, firstDrawFeeCents, recordingFeeCents, closingChargesCents };
    if (netCashCents < 0) {
        fail(offer, `net cash is negative (${netCashCents} cents) at line size ${lineSizeCents} cents`);
    }
    let fixedTermMonthlyPaymentCents = null;
    if (offer.fixedTermMonths == null) {
        if (pricing.fixedTermApr != null) {
            fail(offer, 'has a fixedTermApr but no fixedTermMonths');
        }
    }
    else {
        const fixedTermApr = required(offer, 'fixedTermApr', pricing.fixedTermApr);
        if (!Number.isInteger(offer.fixedTermMonths) || offer.fixedTermMonths <= 0) {
            fail(offer, `fixedTermMonths ${offer.fixedTermMonths} must be a positive integer`);
        }
        // Python upfrontDrawPrincipal: what is drawn less what is paid out of it for closing.
        fixedTermMonthlyPaymentCents = (0, math_1.computeMonthlyPaymentCents)(charges.drawAmountCents - charges.closingChargesCents, fixedTermApr, offer.fixedTermMonths);
    }
    return {
        lineSizeCents,
        ...charges,
        netCashCents,
        refiPayoff: inputs.refiPayoff,
        refiPayoffFeeCents: getQuotedRefiPayoffFeeCents(offer, inputs.refiPayoff),
        debtSwapPayoff: inputs.debtSwapPayoff,
        statedUsageCents: lineSizeCents,
        pricingBand,
        pricing,
        fixedTermMonthlyPaymentCents,
    };
}
/** What a line nets before the lien and debt-swap payoffs, which is `netCashCents` plus them: the slider's cash + payoff axis. */
function cashAndPayoffForLineSizeCents(offer, lineSizeCents) {
    return computeCharges(readQuoteInputs(offer), lineSizeCents, resolvePricingBand(offer, lineSizeCents)).cashAndPayoffCents;
}
/** Whatever `quoteOffer` (or code around it) threw, as the Error an error state shows. */
const toQuoteError = (thrown) => (thrown instanceof Error ? thrown : new Error(String(thrown)));
exports.toQuoteError = toQuoteError;
/** `quoteOffer` that returns its failure as `error`, for UIs that show an error state: never a zero or a default in place of the quote. */
function tryQuoteOffer(offer, lineSizeCents) {
    try {
        return { quote: quoteOffer(offer, lineSizeCents), error: null };
    }
    catch (error) {
        return { quote: null, error: (0, exports.toQuoteError)(error) };
    }
}
/**
 * The single inverse of quoteOffer, for slider UIs and the one-time selection migration only: the smallest line size whose cash +
 * payoff is at least `cashAndPayoffCents` (equal to it, as a cent of line moves it by at most a cent), and never below the offer's
 * smallest band. Null when even the offer's largest line does not reach it.
 */
function lineSizeForCashAndPayoffCents(offer, cashAndPayoffCents) {
    const drawRatio = fractionOfLineDrawn(offer);
    if (!Number.isInteger(cashAndPayoffCents)) {
        fail(offer, `cash and payoff ${cashAndPayoffCents} cents must be a whole number of cents`);
    }
    const inputs = readQuoteInputs(offer);
    const cashAndPayoffAt = (lineSizeCents) => computeCharges(inputs, lineSizeCents, resolvePricingBand(offer, lineSizeCents)).cashAndPayoffCents;
    // Recording depends on the band, so solve each band on its own, lowest first. A non-banded offer is one range up to its own line.
    const bands = [...(offer.lineSizePricingBands ?? [])].sort((a, b) => a.minLineSize - b.minLineSize);
    for (const band of bands.length > 0 ? bands : [null]) {
        const minLineSizeCents = band ? (0, exports.offerDollarsToCents)(band.minLineSize) : 1;
        const maxLineSizeCents = (0, exports.offerDollarsToCents)(band ? band.maxLineSize : required(offer, 'lineSize', offer.lineSize));
        const recordingFixedCents = band?.recordingTaxFree ? 0 : inputs.recordingFixedCents;
        const recordingVariableDecimal = band?.recordingTaxFree ? 0 : inputs.recordingVariableDecimal;
        // cash + payoff = line * (draw ratio * (1 - firstDraw) - origination - recordingVariable) - recordingFixed - signing
        const cashPerLineCent = drawRatio * (1 - inputs.firstDrawFeePercent) - inputs.originationFeePercent - recordingVariableDecimal;
        if (cashPerLineCent <= 0) {
            fail(offer, 'fees consume the whole draw, so no line size nets cash');
        }
        // Clamp-and-confirm: the closed form is only a seed, since per-fee cent rounding moves it a cent either way and
        // it can sit past this band's top even when the band's top itself reaches the target. The forward function decides.
        const seedCents = Math.ceil((cashAndPayoffCents + recordingFixedCents + inputs.effectiveSigningCents) / cashPerLineCent);
        let lineSizeCents = Math.min(maxLineSizeCents, Math.max(minLineSizeCents, seedCents));
        while (lineSizeCents < maxLineSizeCents && cashAndPayoffAt(lineSizeCents) < cashAndPayoffCents) {
            lineSizeCents++;
        }
        if (cashAndPayoffAt(lineSizeCents) < cashAndPayoffCents) {
            continue;
        }
        // Cash grows cashPerLineCent a line, but drops a cent where the rounded recording steps up, so a smaller line can
        // still reach the target up to 1 / cashPerLineCent lines back.
        const lookbackCents = Math.ceil(1 / cashPerLineCent) + 1;
        for (let stepped = true; stepped;) {
            stepped = false;
            for (let below = Math.max(minLineSizeCents, lineSizeCents - lookbackCents); below < lineSizeCents; below++) {
                if (cashAndPayoffAt(below) >= cashAndPayoffCents) {
                    lineSizeCents = below;
                    stepped = true;
                    break;
                }
            }
        }
        return lineSizeCents;
    }
    return null;
}
/** The line size that nets the applicant `netCashCents`, once the offer's own lien and debt-swap payoffs are covered. */
function lineSizeForNetCashCents(offer, netCashCents) {
    if (!Number.isInteger(netCashCents) || netCashCents < 0) {
        fail(offer, `net cash ${netCashCents} cents must be a non-negative whole number of cents`);
    }
    const payoffsCents = getRefiPayoff(offer).totalCents + getAppliedDebtSwapPayoff(offer).totalCents;
    return lineSizeForCashAndPayoffCents(offer, netCashCents + payoffsCents) ?? fail(offer, `no line size nets ${netCashCents} cents`);
}
